/* eslint-disable react/prop-types */
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import { Placement, VirtualElement } from '@popperjs/core';
import {
  StyledOffscreen,
  StyledOffscreenFullInner,
  StyledOffscreenFullOutter,
  StyledOffscreenFullWrap,
  StyledOffscreenOverlay,
  StyledTooltipArrow,
} from './StyledPopover';
import PopoverContainer from './PopoverContainer';
import { mergeRefs } from '@utils/view-layer/react';
export interface PopoverProps {
  arrow?: boolean;
  borderColor?: string;
  borderWidth?: number;
  destroyOnClose?: boolean;
  className?: string;
  width?: number;
  children?: React.ReactNode;
  minWidth?: number;
  contentRef?: any;
  hidden?: boolean;
  id?: string;
  isFullscreenCentered?: boolean;
  isFullHeight?: boolean;
  hasOverlay?: boolean;
  lockBodyScroll?: boolean;
  placement?: Placement;
  pinTo?: 'bottom' | 'top' | 'left';
  referenceElement?: Element | VirtualElement;
  renderMethod?: 'fullscreen' | 'reference';
  zIndex?: number;

  onOk?: () => void;
  onCancel?: () => void;
  afterClose?: () => void;
  beforeClose?: () => any;
  buttons?: (props: PopoverProps) => JSX.Element;
}

/* eslint-disable react/display-name */
export const BEPopover: React.ForwardRefExoticComponent<
  React.PropsWithoutRef<PopoverProps> & React.RefAttributes<HTMLDivElement>
> = forwardRef(
  (
    {
      arrow = false,
      children,
      contentRef,
      hidden = false,
      hasOverlay = false,
      id,
      pinTo,
      placement,
      referenceElement,
      renderMethod = 'reference',
      zIndex = 999,
      width,
      beforeClose,
      onCancel,
      onOk,
      ...rest
    },
    ref
  ) => {
    const [localRef, setLocalRef] = useState<any>();
    const [arrowRef, setArrowRef] = useState<any>();

    const { attributes, styles } = usePopper(
      renderMethod === 'fullscreen' ? document.body : referenceElement,
      localRef,
      {
        modifiers: [
          {
            name: 'arrow',
            options: {
              element: arrowRef,
            },
          },
          {
            name: 'offset',
            options: {
              offset: [0, 6],
            },
          },
        ],
        placement,
        strategy: 'fixed',
      }
    );

    const close = useCallback(async () => {
      if (beforeClose && !(await beforeClose())) {
        return;
      }
      onCancel?.();
    }, [beforeClose, onCancel]);

    const style = useMemo(() => {
      if (pinTo === 'bottom') {
        return {
          ...styles.popper,
          bottom: 0,
          top: undefined,
        };
      }

      if (pinTo === 'top') {
        return {
          ...styles.popper,
          bottom: undefined,
          top: 0,
        };
      }

      if (pinTo === 'left') {
        return {
          ...styles.popper,
          bottom: 0,
          left: 0,
          top: 0,
        };
      }

      return { ...styles.popper };
    }, [pinTo, styles.popper]);

    const transform = useMemo(() => {
      if (pinTo === 'bottom' || pinTo === 'top' || pinTo === 'left') {
        return undefined;
      }

      return styles.popper.transform;
    }, [pinTo, styles.popper.transform]);

    if (renderMethod === 'fullscreen') {
      return (
        <StyledOffscreen
          {...attributes.popper}
          id={id}
          ref={setLocalRef}
          role="dialog"
          style={{ ...style, width, transform, zIndex }}
        >
          <StyledOffscreenFullWrap>
            <StyledOffscreenFullOutter>
              {hasOverlay && (
                <StyledOffscreenOverlay
                  style={{ animation: hidden ? 'none' : undefined }}
                />
              )}

              <StyledOffscreenFullInner pinTo={pinTo}>
                <PopoverContainer
                  {...rest}
                  renderMethod={renderMethod}
                  ref={ref}
                >
                  {children}
                </PopoverContainer>
              </StyledOffscreenFullInner>
            </StyledOffscreenFullOutter>
          </StyledOffscreenFullWrap>
        </StyledOffscreen>
      );
    }

    return (
      <StyledOffscreen
        id={id}
        ref={setLocalRef}
        role="dialog"
        style={{ ...style, width, transform, zIndex }}
      >
        <PopoverContainer
          arrow={arrow}
          {...rest}
          onCancel={close}
          referenceElement={referenceElement}
          ref={mergeRefs(ref, contentRef)}
          {...attributes.popper}
        >
          {children}
          {arrow ? (
            <StyledTooltipArrow
              ref={setArrowRef}
              style={{ ...styles.arrow }}
              borderColor={rest.borderColor}
            />
          ) : null}
        </PopoverContainer>
      </StyledOffscreen>
    );
  }
);
